import { atom } from 'jotai';

import {
  grantUserAdminAccessMutationAtom,
  revokeUserAdminAccessMutationAtom,
} from '../_graphql/mutationUserAdminAccess';
import { organizationSearchQueryAtom } from '../_graphql/queryOrganizationSearch';

import { $users } from './userList.state';

// 🔘🔘🔘 TYPES
export type ManageAdminAccessFormData = {
  userId: string;
  userName: string;
  selectedOrganizationId: string;
};

// 🔘🔘🔘 INITIAL STATE
const getInitialFormData = (): ManageAdminAccessFormData => ({
  userId: '',
  userName: '',
  selectedOrganizationId: '',
});

// 🔘🔘🔘 PLAIN ATOMS
export const $manageAdminAccessDialogOpen = atom(false);
export const $manageAdminAccessFormData =
  atom<ManageAdminAccessFormData>(getInitialFormData());

// 🟡🟡🟡 HIDDEN ACTION -> GRANT admin access
export const $grantAdminAccess = grantUserAdminAccessMutationAtom();

// 🟡🟡🟡 HIDDEN ACTION -> REVOKE admin access
export const $revokeAdminAccess = revokeUserAdminAccessMutationAtom();

// 🟠🟠🟠 ACTION -> OPEN dialog
export const $openManageAdminAccessDialog = atom(
  null,
  (
    _get,
    set,
    user: {
      id: string;
      firstName: string;
      lastName: string;
    },
  ) => {
    set($manageAdminAccessFormData, {
      userId: user.id,
      userName: `${user.firstName} ${user.lastName}`,
      selectedOrganizationId: '',
    });
    set($manageAdminAccessDialogOpen, true);
  },
);

// 🟠🟠🟠 ACTION -> CLOSE dialog
export const $closeManageAdminAccessDialog = atom(null, (_get, set) => {
  set($manageAdminAccessDialogOpen, false);
  set($manageAdminAccessFormData, getInitialFormData());
});

// 🟠🟠🟠 ACTION -> UPDATE form data
export const $updateManageAdminAccessFormData = atom(
  null,
  (get, set, update: Partial<ManageAdminAccessFormData>) => {
    const currentData = get($manageAdminAccessFormData);
    set($manageAdminAccessFormData, {
      ...currentData,
      ...update,
    });
  },
);

// 🟠🟠🟠 ACTION -> GRANT admin access
export const $grantAdminAccessAction = atom(null, async (get, set) => {
  const formData = get($manageAdminAccessFormData);

  await set($grantAdminAccess, {
    organizationId: formData.selectedOrganizationId,
    userId: formData.userId,
  });

  // Refresh user list and close dialog
  get($users).refetch();
  set($manageAdminAccessDialogOpen, false);
  set($manageAdminAccessFormData, getInitialFormData());
});

// 🟠🟠🟠 ACTION -> REVOKE admin access
export const $revokeAdminAccessAction = atom(null, async (get, set) => {
  const formData = get($manageAdminAccessFormData);

  await set($revokeAdminAccess, {
    organizationId: formData.selectedOrganizationId,
    userId: formData.userId,
  });

  // Refresh user list and close dialog
  get($users).refetch();
  set($manageAdminAccessDialogOpen, false);
  set($manageAdminAccessFormData, getInitialFormData());
});

// 🟢🟢🟢 COMPUTED DATA -> available organizations
export const $availableOrganizations = organizationSearchQueryAtom((_get) => ({
  variables: {
    searchTerm: undefined,
    limit: 50,
  },
}));

// 🟢🟢🟢 COMPUTED DATA -> form validation
export const $manageAdminAccessFormValid = atom((get) => {
  const formData = get($manageAdminAccessFormData);
  return !!(formData.userId && formData.selectedOrganizationId);
});

// 🟢🟢🟢 COMPUTED DATA -> is saving
export const $manageAdminAccessDialogSaving = atom((get) => {
  const grantStatus = get($grantAdminAccess);
  const revokeStatus = get($revokeAdminAccess);
  return grantStatus.isPending || revokeStatus.isPending;
});
